import { SoldProductDetailsResponse, SoldProductItemDetails } from '../../../../../hooks';
import { SoldProductItemRankedAreaSalesMarket } from '../sales.util';

type SoldProductItemDetailsWithRank = SoldProductItemDetails & {
  rank: number;
};

type ProductsWithRank = {
  store: SoldProductItemDetailsWithRank[];
  area: SoldProductItemDetailsWithRank[];
  salesMarket: SoldProductItemDetailsWithRank[];
};

// Rank products based on net sales
const getProductItemsWithRank = (products: SoldProductItemDetails[]) => {
  const sortedProducts = [...products].sort((a, b) => b.netSale - a.netSale);

  let currentRank = 0;
  let lastPrice: number | null = null;

  const rankedProducts = sortedProducts.map((item, index) => {
    if (item.netSale !== lastPrice) {
      currentRank = index + 1;
      lastPrice = item.netSale;
    }

    return {
      ...item,
      rank: currentRank,
    };
  });

  return rankedProducts;
};

// Compute rankings vs Area and vs Sales Market and get table data
const getProductsRankingVsAreaVsMarket = (
  storeItems: SoldProductItemDetailsWithRank[],
  areaItems: SoldProductItemDetailsWithRank[],
  salesMarketItems: SoldProductItemDetailsWithRank[],
): SoldProductItemRankedAreaSalesMarket[] => {
  const products = storeItems.map((storeProductItem) => {
    const {
      productId,
      productName,
      departmentNumber,
      departmentName,
      subIndexId,
      subIndexDescription,
      price,
      netSale,
      soldPieces,
      netSaleLastWeek,
      rank,
      articleId,
    } = storeProductItem;

    const areaProductItem = areaItems.find((area) => area.productId === productId) || null;
    const marketProductItem = salesMarketItems.find((market) => market.productId === productId) || null;

    let rankingVsArea = 0;
    let rankingVsSalesMarket = 0;

    if (areaProductItem?.rank != null && rank != null) {
      rankingVsArea = areaProductItem.rank - rank;
    }
    if (marketProductItem?.rank != null && rank != null) {
      rankingVsSalesMarket = marketProductItem.rank - rank;
    }

    const differenceLastLastWeek = netSaleLastWeek === 0 ? 0 : (netSale / netSaleLastWeek - 1) * 100;

    return {
      productId,
      productName,
      departmentNumber,
      departmentName,
      subIndexId,
      subIndexDescription,
      articleIdentifier: articleId,
      price,
      netSale,
      soldPieces,
      differenceLastLastWeek,
      rankingVsArea,
      rankingVsSalesMarket,
    };
  });

  return products;
};

export const transformSoldProductDataWithRanking = (
  data: SoldProductDetailsResponse,
  filterIndex?: string,
): SoldProductItemRankedAreaSalesMarket[] => {
  let productData: ProductsWithRank = {
    store: getProductItemsWithRank([...data.store]),
    area: getProductItemsWithRank([...data.area]),
    salesMarket: getProductItemsWithRank([...data.salesMarket]),
  };

  if (filterIndex) {
    productData = {
      store: productData.store.filter((item) => item.indexId === filterIndex),
      area: productData.area.filter((item) => item.indexId === filterIndex),
      salesMarket: productData.salesMarket.filter((item) => item.indexId === filterIndex),
    };
  }

  // Compute vs Area and vs Sales Market rankings
  const productsRankedVsAreaVsSalesMarket = getProductsRankingVsAreaVsMarket(
    productData.store,
    productData.area,
    productData.salesMarket,
  );

  return productsRankedVsAreaVsSalesMarket;
};

export const getWeeklyTop20ProductsData = (
  data: SoldProductDetailsResponse,
  filterIndex?: string,
): SoldProductItemRankedAreaSalesMarket[] => {
  const soldProductsRankedVsAreaVsSalesMarket = transformSoldProductDataWithRanking(data, filterIndex);

  // Sort and get top 20 products by net sales
  const sortedItems = soldProductsRankedVsAreaVsSalesMarket.sort((a, b) => b.netSale - a.netSale);
  const top20Products = sortedItems.slice(0, 20);

  return top20Products;
};
